import { useEffect } from "react";
import { Mail, ArrowUpRight } from "lucide-react";
import {
  LegalLayout,
  Section,
  SubList,
} from "../components/legal/LegalLayout";

const DIRECTORY = [
  {
    id: "general",
    label: "General",
    email: "contact@ivory-pulse.com",
    note: "General questions and anything that doesn't clearly fit another team.",
  },
  {
    id: "support",
    label: "Support",
    email: "support@ivory-pulse.com",
    note: "Customer support, account help, technical issues and order questions.",
  },
  {
    id: "media",
    label: "Media",
    email: "media@ivory-pulse.com",
    note: "Press, interviews, brand assets and media enquiries.",
  },
  {
    id: "investors",
    label: "Investors",
    email: "investors@ivory-pulse.com",
    note: "Investor relations and funding conversations.",
  },
  {
    id: "business",
    label: "Business",
    email: "sales@ivory-pulse.com",
    note: "Partnerships, retail, distribution and business development.",
  },
];

export default function Contact() {
  useEffect(() => {
    if (window.location.hash) {
      const target = window.location.hash;
      setTimeout(() => {
        document.querySelector(target)?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 150);
    }
  }, []);

  return (
    <LegalLayout
      title="Contact"
      kicker="Corporate"
      updated="August 11, 2026"
      pageTestId="page-contact"
    >
      <Section title="Contact directory">
        <p>
          Email the team most relevant to your request so we can reply
          quickly. We aim to respond to enquiries within a few business days.
        </p>
        <div className="mt-2 grid gap-3 sm:grid-cols-2">
          {DIRECTORY.map((d) => (
            <a
              key={d.id}
              id={d.id}
              href={`mailto:${d.email}`}
              data-testid={`contact-email-${d.id}`}
              className="group scroll-mt-24 rounded-none border border-white/10 bg-[#0A0A0A] px-5 py-4 transition-colors duration-300 hover:border-[#C7A962]/40"
            >
              <span className="flex items-center justify-between">
                <span className="font-mono text-[9.5px] uppercase tracking-[0.2em] text-white/40 transition-colors group-hover:text-[#C7A962]">
                  {d.label}
                </span>
                <ArrowUpRight className="h-4 w-4 text-white/25 transition-colors group-hover:text-[#C7A962]" />
              </span>
              <span className="mt-1.5 flex items-center gap-2 text-[14.5px] font-medium text-white">
                <Mail className="h-3.5 w-3.5 text-[#C7A962]" />
                {d.email}
              </span>
              <span className="mt-1.5 block text-[12.5px] leading-relaxed text-white/45">
                {d.note}
              </span>
            </a>
          ))}
        </div>
      </Section>

      <Section title="Company details">
        <SubList
          items={[
            "Legal name: IVORY PULSE SRL",
            "Registration (CUI): 52619680",
            "Registered office: Romania",
          ]}
        />
      </Section>

      <Section title="What to include in your email">
        <SubList
          items={[
            "Your full name and the email address you used on the waitlist.",
            "A clear description of your question or request.",
            "If you are asking about an order once pre-sale opens, please include your order reference.",
            "If you are exercising a privacy right (access, deletion, correction, objection, consent withdrawal), please state so explicitly.",
          ]}
        />
      </Section>

      <Section title="Legal requests">
        <p>
          For formal legal notices, takedown requests or law-enforcement
          enquiries, please send them in writing to{" "}
          <a
            className="underline decoration-white/30 hover:decoration-white"
            href="mailto:legal@ivory-pulse.com"
          >
            legal@ivory-pulse.com
          </a>{" "}
          with the subject line starting with "Legal:". We will acknowledge
          receipt and route the request to the appropriate team.
        </p>
      </Section>

      <Section title="About Ivory Pulse">
        <p>
          Pulse One is a consumer wearable AI companion bracelet designed
          around standalone connectivity. It is <strong>not</strong> a
          medical device, fitness tracker, pulse monitor, sleep tracker or
          healthcare product and must not be used or represented as such.
        </p>
      </Section>
    </LegalLayout>
  );
}
